import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserOps } from '@towns/userops'
import {
    Address,
    staleTime24Hours,
    useBaseWrites,
    useConnectivity,
    useOfflineStore,
} from 'use-towns-client'

import { useEnvironment } from './useEnvironment'

const queryKey = (args: {
    isAccountAbstractionEnabled: boolean
    rootKeyAddress: Address | undefined
}) => ['smartAccountAddress', args.isAccountAbstractionEnabled, args.rootKeyAddress]

// Global flag to track if we've validated the address during this app session
let hasValidatedMyAddress = false

export async function getAbstractAccountAddress({
    rootKeyAddress,
    isMyAbstractAccount = false,
    isAccountAbstractionEnabled,
    userOpsInstance,
}: {
    rootKeyAddress: Address | undefined
    isMyAbstractAccount?: boolean
    isAccountAbstractionEnabled: boolean
    userOpsInstance: UserOps | undefined
}) {
    // if account abstraction is not enabled, we're using the root key address
    if (!isAccountAbstractionEnabled) {
        return rootKeyAddress
    }

    if (!rootKeyAddress || !userOpsInstance) {
        return
    }

    const cachedAddress = getCachedAddress({ rootKeyAddress })

    // for my own abstract account address, i want get the uncached address a single time
    // if that is different from the cached address, update the cache
    // they would be different if the user has a client that cached the simple account address, but they never did any ops

    // TODO: probably we can skip caching my own abstract account address entirely,
    // but unsure if useOfflineStore might be using it in any other context, so just being safe
    if (isMyAbstractAccount && !hasValidatedMyAddress) {
        const fetchedAddress = await userOpsInstance.smartAccountSession.getAbstractAccountAddress({
            rootKeyAddress,
        })

        hasValidatedMyAddress = true

        // If fetched address exists and differs from cache, update the cache
        if (
            fetchedAddress &&
            (!cachedAddress || fetchedAddress.toLowerCase() !== cachedAddress.toLowerCase())
        ) {
            useOfflineStore.getState().setOfflineWalletAddress(rootKeyAddress, fetchedAddress)
            return fetchedAddress
        }
    }

    // other users abstract account address, we would have only encountered and cached them if we joined a town with them
    // that they've joined a town means they've done ops, so their smart account contract was deployed, and the server would return the same address
    if (cachedAddress) {
        return cachedAddress
    }

    const returnVal = await userOpsInstance.smartAccountSession.getAbstractAccountAddress({
        rootKeyAddress,
    })

    if (returnVal) {
        useOfflineStore.getState().setOfflineWalletAddress(rootKeyAddress, returnVal)
    }
    return returnVal
}

export function useAbstractAccountAddress({
    rootKeyAddress,
    isMyAbstractAccount = false,
}: {
    rootKeyAddress: Address | undefined
    isMyAbstractAccount?: boolean
}) {
    const { accountAbstractionConfig } = useEnvironment()
    const { userOpsInstance } = useBaseWrites()

    const isAccountAbstractionEnabled = accountAbstractionConfig !== undefined

    const { data, isLoading, isError } = useQuery({
        queryKey: queryKey({ isAccountAbstractionEnabled, rootKeyAddress }),
        queryFn: () =>
            getAbstractAccountAddress({
                rootKeyAddress,
                isMyAbstractAccount,
                isAccountAbstractionEnabled,
                userOpsInstance,
            }),
        enabled: !!rootKeyAddress,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        staleTime: staleTime24Hours,
    })

    return useMemo(() => {
        return {
            data,
            isLoading,
            isError,
        }
    }, [data, isLoading, isError])
}

export function isAbstractAccountAddress({
    address,
    abstractAccountAddress,
}: {
    address: Address
    abstractAccountAddress: Address | undefined
}) {
    return address.toLowerCase() === abstractAccountAddress?.toLowerCase()
}

export function getCachedAddress(args: { rootKeyAddress: string | undefined }) {
    const { rootKeyAddress } = args
    if (!rootKeyAddress) {
        return
    }
    const offlineWalletAddressMap = useOfflineStore.getState().offlineWalletAddressMap
    return offlineWalletAddressMap[rootKeyAddress] as Address
}

export function useMyAbstractAccountAddress() {
    const { loggedInWalletAddress } = useConnectivity()
    return useAbstractAccountAddress({
        rootKeyAddress: loggedInWalletAddress,
        isMyAbstractAccount: true,
    })
}

export async function ensureMyAbstractAccountAddress({
    queryClient,
    rootKeyAddress,
    userOpsInstance,
}: {
    queryClient: ReturnType<typeof useQueryClient>
    rootKeyAddress: Address | undefined
    userOpsInstance: UserOps | undefined
}): Promise<Address | undefined> {
    if (!rootKeyAddress) {
        return undefined
    }

    return queryClient.ensureQueryData({
        queryKey: queryKey({ isAccountAbstractionEnabled: true, rootKeyAddress }),
        queryFn: () =>
            getAbstractAccountAddress({
                rootKeyAddress,
                isMyAbstractAccount: true,
                isAccountAbstractionEnabled: true,
                userOpsInstance,
            }),
        staleTime: staleTime24Hours,
    })
}
